import {
    Component,
    Output,
    EventEmitter,
    ViewChild,
    OnInit,
    OnDestroy,
    Input,
    Injector,
    signal
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import { piIconWorldBold } from '@predictiveindex/pi-client-icons';
import {
    IModule,
    IconSize,
    TapestryLayoutService,
    TapestrySidenavService,
    TooltipPosition,
    TapestryIconRegistryService
} from '@predictiveindex/tapestry';
import { Subscription } from 'rxjs';
import { PI, groupAnalyticsPath } from '../../constants-types';
import {
    ClassificationType,
    SellerToolType,
    SellerToolsPeekData
} from '../../core-data-model';
import { SiteNavigationService } from '../../core-services';
import { MyProfileTooltipService } from '../../my-profile-tooltip';
import {
    ModuleAccessService,
    FeatureFlagsService,
    FeatureFlags,
    ModuleUserAccessService,
    InitDataService
} from '../../system-services';
import { TranslationService } from '../../translations';
import { GlobalNavService } from './global-nav.service';
import {
    SELLER_TOOL_PEEK_DATA,
    SellerToolsBaseComponent
} from './seller-tools/seller-tools-base.component';

@Component({
    selector: 'global-nav',
    templateUrl: './global-nav.component.html',
    styleUrls: ['./global-nav.component.less'],
    standalone: false
})
export class GlobalNavComponent implements OnInit, OnDestroy {
    @Input()
    disableAll = false;

    @Output()
    onUpgradeAccountClick: EventEmitter<any> = new EventEmitter();

    @Output()
    piAssistantClick: EventEmitter<void> = new EventEmitter();

    @Output()
    sellerToolsClick: EventEmitter<void> = new EventEmitter();

    @Output()
    moduleClick: EventEmitter<void> = new EventEmitter();

    @ViewChild('matMenuTriggerClick') matMenuTriggerClick: MatMenuTrigger;

    IconSize = IconSize;
    customAvatarsEnabled = false;
    TooltipPosition = TooltipPosition;
    myProfileOpen = false;
    languageSelectionOpen = false;
    navigationModules: IModule[];

    isDevMenuEnabled = false;
    isInternalOIEnabled = false;
    hideGlobalAdmin = false;
    hideGroupAnalytics = false;
    hidePricingPackaging = true;
    enableLanguageSelection = signal(false);
    enablePIAssistant = signal(false);

    private showProfileSubscription$: Subscription;

    constructor(
        public TR: TranslationService,
        public moduleAccess: ModuleAccessService,
        public siteNavigation: SiteNavigationService,
        private tapestrySidenavService: TapestrySidenavService,
        private featureFlagService: FeatureFlagsService,
        public layout: TapestryLayoutService,
        private myProfileTooltipService: MyProfileTooltipService,
        public globalNavService: GlobalNavService,
        private readonly router: Router,
        private readonly moduleUserAccess: ModuleUserAccessService,
        private readonly tapestryIconRegistry: TapestryIconRegistryService,
        private readonly initDataService: InitDataService
    ) {}

    ngOnInit(): void {
        this.setupFeatureFlags();
        this.navigationModules = this.disableAll
            ? []
            : this.wrapModulesWithClickEmitter(
                  this.globalNavService.availableModules
              );
        this.showProfileSubscription$ =
            this.myProfileTooltipService.showProfile$.subscribe(
                (showProfile: boolean) => {
                    if (showProfile && this.siteNavigation.isInHire) {
                        this.matMenuTriggerClick.openMenu();
                    }
                }
            );
        this.isDevMenuEnabled =
            PI.appSettings.environmentName === 'dev' ||
            PI.appSettings.environmentName === 'develop';
        this.tapestryIconRegistry.registerIcons([piIconWorldBold]);
    }

    ngOnDestroy(): void {
        this.showProfileSubscription$.unsubscribe();
    }

    setupFeatureFlags(): void {
        this.customAvatarsEnabled = this.featureFlagService.getByFlag(
            FeatureFlags.enableCustomAvatars
        );

        this.isInternalOIEnabled =
            this.moduleAccess.accountClassificationType ===
            ClassificationType.Seller;

        this.hideGlobalAdmin = !this.userIsAdmin();

        this.hideGroupAnalytics =
            this.featureFlagService.getByFlag(
                FeatureFlags.hideGroupAnalyticsNav
            ) && !this.userIsAdmin();

        this.enableLanguageSelection.set(
            this.featureFlagService.getByFlag(
                FeatureFlags.enableLanguageSelection
            )
        );

        const isFeatureFlagEnabled = this.featureFlagService.getByStringFlag(
            'platform.enablePIAssistant'
        );
        const isAssistantDisabled =
            this.initDataService.companySettings?.disableAiAssistant;

        this.enablePIAssistant.set(
            isFeatureFlagEnabled && !isAssistantDisabled
        );
    }

    userIsAdmin(): boolean {
        return this.moduleUserAccess.hasAccessLevelForAnyModule(
            PermissionsModuleAccessLevel.AllModuleAdmin
        );
    }

    triggerUpgradeAccountClick() {
        if (this.siteNavigation.isInHire) {
            this.onUpgradeAccountClick.emit();
        }
    }



    openMyProfile() {
        this.matMenuTriggerClick.toggleMenu();
    }

    openOIPeek() {
        this.sellerToolsClick.emit();
        this.tapestrySidenavService.open({
            component: SellerToolsBaseComponent,
            title: this.TR.key('client-core.global-nav.partner-support.title'),
            injector: Injector.create({
                providers: [
                    {
                        provide: SELLER_TOOL_PEEK_DATA,
                        useValue: <SellerToolsPeekData>{
                            sellerToolType: SellerToolType.OpenInvitation,
                            prospectData: []
                        }
                    }
                ]
            }),
            helpLink: {
                displayName: this.TR.key(
                    'client-core.global-nav.partner-support.help-link'
                ),
                url: 'https://predictiveindex.my.site.com/partners/'
            }
        });
    }

    goToGroupAnalytics() {
        this.router.navigate([`${groupAnalyticsPath}`]);
    }

    closeMyProfile() {
        this.myProfileOpen = false;
    }

    closeLanguageSelection() {
        this.languageSelectionOpen = false;
    }

    openPIAssistant(): void {
        this.piAssistantClick.emit();
    }

    private wrapModulesWithClickEmitter(modules: IModule[]): IModule[] {
        return modules.map((module) => ({
            ...module,
            onClickAction: () => {
                this.moduleClick.emit();
                module.onClickAction();
            }
        }));
    }
}
